<div class="jira-reports-container">
  <div class="header-section">
    <h1>JIRA Project Reports & Analytics</h1>
    <p class="subtitle">Upload your JIRA CSV export to analyze sprint performance, velocity, and burn-down charts</p>
  </div>

  <!-- File Upload Section -->
  <mat-card class="upload-section">
    <mat-card-header>
      <mat-card-title>
        <mat-icon>upload_file</mat-icon>
        Upload JIRA CSV Export
      </mat-card-title>
      <mat-card-subtitle>Select your JIRA CSV file to begin analysis</mat-card-subtitle>
    </mat-card-header>
    <mat-card-content>
      <div class="upload-area">
        <input type="file" 
               #fileInput 
               (change)="onFileSelected($event)" 
               accept=".csv"
               style="display: none;">
        <button mat-raised-button 
                color="primary" 
                (click)="fileInput.click()"
                class="upload-button">
          <mat-icon>cloud_upload</mat-icon>
          Choose CSV File
        </button>
        <p class="upload-hint">Supported format: JIRA CSV export with Story Points, Time Tracking, and Sprint data</p>
      </div>
    </mat-card-content>
  </mat-card>

  <!-- Summary Statistics -->
  <div class="stats-section" *ngIf="jiraData.length > 0">
    <mat-card class="stat-card">
      <mat-card-content>
        <div class="stat-value">{{ totalIssues }}</div>
        <div class="stat-label">Total Issues</div>
      </mat-card-content>
    </mat-card>
    
    <mat-card class="stat-card">
      <mat-card-content>
        <div class="stat-value">{{ totalStoryPoints }}</div>
        <div class="stat-label">Total Story Points</div>
      </mat-card-content>
    </mat-card>
    
    <mat-card class="stat-card">
      <mat-card-content>
        <div class="stat-value">{{ totalTimeSpent | number:'1.0-1' }}h</div>
        <div class="stat-label">Total Hours Spent</div>
      </mat-card-content>
    </mat-card>
    
    <mat-card class="stat-card">
      <mat-card-content>
        <div class="stat-value">{{ averageVelocity | number:'1.0-1' }}</div>
        <div class="stat-label">Avg Velocity</div>
      </mat-card-content>
    </mat-card>
  </div>

  <!-- Project Filter -->
  <mat-card class="filter-section" *ngIf="projects.length > 0">
    <mat-card-content>
      <div class="filter-controls">
        <mat-form-field appearance="outline">
          <mat-label>Filter by Project</mat-label>
          <mat-select [(value)]="selectedProject" (selectionChange)="filterByProject($event.value)">
            <mat-option value="">All Projects</mat-option>
            <mat-option *ngFor="let project of projects" [value]="project">
              {{ project }}
            </mat-option>
          </mat-select>
        </mat-form-field>
        
        <mat-form-field appearance="outline">
          <mat-label>Filter by Sprint</mat-label>
          <mat-select [(value)]="selectedSprint" (selectionChange)="filterBySprint($event.value)">
            <mat-option value="">All Sprints</mat-option>
            <mat-option *ngFor="let sprint of sprints" [value]="sprint">
              {{ sprint }}
            </mat-option>
          </mat-select>
        </mat-form-field>
        
        <mat-form-field appearance="outline">
          <mat-label>Search Issues</mat-label>
          <input matInput (keyup)="applyFilter($event)" placeholder="Search by summary, issue key, etc.">
          <mat-icon matSuffix>search</mat-icon>
        </mat-form-field>
        
        <button mat-raised-button 
                color="accent" 
                (click)="exportToCSV()"
                class="export-button">
          <mat-icon>download</mat-icon>
          Export CSV
        </button>
      </div>
    </mat-card-content>
  </mat-card>

  <!-- Charts Section -->
  <div class="charts-section" *ngIf="jiraData.length > 0">
    <div class="chart-row">
      <mat-card class="chart-card">
        <mat-card-header>
          <mat-card-title>Sprint Velocity</mat-card-title>
          <mat-card-subtitle>Story points completed per sprint with average line</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content>
          <canvas id="velocityChart"></canvas>
        </mat-card-content>
      </mat-card>
      
      <mat-card class="chart-card">
        <mat-card-header>
          <mat-card-title>Story Points by Status</mat-card-title>
          <mat-card-subtitle>Distribution of story points across issue statuses</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content>
          <canvas id="storyPointsChart"></canvas>
        </mat-card-content>
      </mat-card>
    </div>
    
    <div class="chart-row">
      <mat-card class="chart-card">
        <mat-card-header>
          <mat-card-title>Time Tracking Overview</mat-card-title>
          <mat-card-subtitle>Original estimate vs time spent vs remaining</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content>
          <canvas id="timeTrackingChart"></canvas>
        </mat-card-content>
      </mat-card>
      
      <mat-card class="chart-card">
        <mat-card-header>
          <mat-card-title>Overall Progress</mat-card-title>
          <mat-card-subtitle>Planned vs completed vs in progress items</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content>
          <canvas id="overallProgressChart"></canvas>
        </mat-card-content>
      </mat-card>
    </div>
    
    <div class="chart-row">
      <mat-card class="chart-card full-width">
        <mat-card-header>
          <mat-card-title>Burndown Chart</mat-card-title>
          <mat-card-subtitle>Actual vs ideal burndown for sprint completion</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content>
          <canvas id="burndownChart"></canvas>
        </mat-card-content>
      </mat-card>
    </div>
  </div>

  <!-- Sprint Summary Table -->
  <mat-card class="sprint-summary" *ngIf="sprintData.length > 0">
    <mat-card-header>
      <mat-card-title>Sprint Summary</mat-card-title>
      <mat-card-subtitle>Overview of sprint performance metrics</mat-card-subtitle>
    </mat-card-header>
    <mat-card-content>
      <table mat-table [dataSource]="sprintData" matSort class="sprint-table">
        <ng-container matColumnDef="sprintName">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Sprint</th>
          <td mat-cell *matCellDef="let sprint">{{ sprint.sprintName }}</td>
        </ng-container>
        
        <ng-container matColumnDef="startDate">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Start Date</th>
          <td mat-cell *matCellDef="let sprint">{{ sprint.startDate | date:'MMM yyyy' }}</td>
        </ng-container>
        
        <ng-container matColumnDef="totalIssues">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Total Issues</th>
          <td mat-cell *matCellDef="let sprint">{{ sprint.totalIssues }}</td>
        </ng-container>
        
        <ng-container matColumnDef="completedIssues">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Completed</th>
          <td mat-cell *matCellDef="let sprint">{{ sprint.completedIssues }}</td>
        </ng-container>
        
        <ng-container matColumnDef="commitment">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Commitment</th>
          <td mat-cell *matCellDef="let sprint">{{ sprint.commitment }}</td>
        </ng-container>
        
        <ng-container matColumnDef="completed">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Completed</th>
          <td mat-cell *matCellDef="let sprint">{{ sprint.completed }}</td>
        </ng-container>
        
        <ng-container matColumnDef="velocity">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Velocity</th>
          <td mat-cell *matCellDef="let sprint">{{ sprint.velocity }}</td>
        </ng-container>
        
        <ng-container matColumnDef="completionRate">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Completion Rate</th>
          <td mat-cell *matCellDef="let sprint">
            {{ sprint.commitment > 0 ? (sprint.completed / sprint.commitment * 100 | number:'1.0-1') : 0 }}%
          </td>
        </ng-container>
        
        <tr mat-header-row *matHeaderRowDef="['sprintName', 'startDate', 'totalIssues', 'completedIssues', 'commitment', 'completed', 'velocity', 'completionRate']"></tr>
        <tr mat-row *matRowDef="let row; columns: ['sprintName', 'startDate', 'totalIssues', 'completedIssues', 'commitment', 'completed', 'velocity', 'completionRate'];"></tr>
      </table>
    </mat-card-content>
  </mat-card>

  <!-- Issues Data Table -->
  <mat-card class="issues-table" *ngIf="jiraData.length > 0">
    <mat-card-header>
      <mat-card-title>Issues Details</mat-card-title>
      <mat-card-subtitle>Complete list of issues with time tracking and story points</mat-card-subtitle>
    </mat-card-header>
    <mat-card-content>
      <table mat-table [dataSource]="dataSource" matSort class="issues-table-content">
        <!-- Issue Key Column -->
        <ng-container matColumnDef="issueKey">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Issue Key</th>
          <td mat-cell *matCellDef="let issue">{{ issue.issueKey }}</td>
        </ng-container>
        
        <!-- Summary Column -->
        <ng-container matColumnDef="summary">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Summary</th>
          <td mat-cell *matCellDef="let issue" class="summary-cell">{{ issue.summary }}</td>
        </ng-container>
        
        <!-- Issue Type Column -->
        <ng-container matColumnDef="issueType">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Type</th>
          <td mat-cell *matCellDef="let issue">{{ issue.issueType }}</td>
        </ng-container>
        
        <!-- Status Column -->
        <ng-container matColumnDef="status">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Status</th>
          <td mat-cell *matCellDef="let issue">
            <span class="status-badge" [class]="'status-' + issue.status.toLowerCase().replace(' ', '-')">
              {{ issue.status }}
            </span>
          </td>
        </ng-container>
        
        <!-- Sprint Column -->
        <ng-container matColumnDef="sprint">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Sprint</th>
          <td mat-cell *matCellDef="let issue">{{ issue.sprint }}</td>
        </ng-container>
        
        <!-- Story Points Column -->
        <ng-container matColumnDef="storyPoints">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Story Points</th>
          <td mat-cell *matCellDef="let issue">{{ issue.storyPoints }}</td>
        </ng-container>
        
        <!-- Original Estimate Column -->
        <ng-container matColumnDef="originalEstimate">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Original Estimate</th>
          <td mat-cell *matCellDef="let issue">{{ formatTime(issue.originalEstimate) }}</td>
        </ng-container>
        
        <!-- Time Spent Column -->
        <ng-container matColumnDef="timeSpent">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Time Spent</th>
          <td mat-cell *matCellDef="let issue">{{ formatTime(issue.timeSpent) }}</td>
        </ng-container>
        
        <!-- Remaining Estimate Column -->
        <ng-container matColumnDef="remainingEstimate">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Remaining</th>
          <td mat-cell *matCellDef="let issue">{{ formatTime(issue.remainingEstimate) }}</td>
        </ng-container>
        
        <!-- Work Ratio Column -->
        <ng-container matColumnDef="workRatio">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Work Ratio</th>
          <td mat-cell *matCellDef="let issue">{{ issue.workRatio | number:'1.0-1' }}%</td>
        </ng-container>
        
        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
        <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
      </table>
      
      <mat-paginator [pageSizeOptions]="[10, 25, 50, 100]" showFirstLastButtons></mat-paginator>
    </mat-card-content>
  </mat-card>

  <!-- Empty State -->
  <div class="empty-state" *ngIf="jiraData.length === 0">
    <mat-card>
      <mat-card-content>
        <div class="empty-content">
          <mat-icon class="empty-icon">analytics</mat-icon>
          <h3>No Data Available</h3>
          <p>Upload a JIRA CSV export to see your project analytics, sprint performance, and burn-down charts.</p>
          <button mat-raised-button 
                  color="primary" 
                  (click)="fileInput.click()"
                  class="upload-button">
            <mat-icon>upload_file</mat-icon>
            Upload CSV File
          </button>
        </div>
      </mat-card-content>
    </mat-card>
  </div>
</div>
